import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Trophy, Medal, Award, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { LeaderboardEntry } from '../../services/leaderboardService';
import { UserProfile, getUserProfile } from '../../services/userProfileService';
import { LeaderboardRowExpanded } from './LeaderboardRowExpanded';

interface LeaderboardMobileCardProps {
  entry: LeaderboardEntry;
  isExpanded: boolean;
  onToggle: () => void;
}

const getRankIcon = (rank: number) => {
  if (rank === 1) return <Trophy className="h-5 w-5 text-yellow-500" />;
  if (rank === 2) return <Medal className="h-5 w-5 text-gray-400" />;
  if (rank === 3) return <Award className="h-5 w-5 text-amber-600" />;
  return null;
};

export const LeaderboardMobileCard: React.FC<LeaderboardMobileCardProps> = ({
  entry,
  isExpanded,
  onToggle,
}) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Fetch profile when card is expanded
  useEffect(() => {
    if (isExpanded && !profile && !loading && !error) {
      fetchProfile();
    }
  }, [isExpanded]);

  // Handle animation state
  useEffect(() => {
    if (isExpanded) {
      setIsAnimating(true);
    } else if (isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isExpanded, isAnimating]);

  // Auto-scroll to expanded content
  useEffect(() => {
    if (isExpanded && isAnimating && cardRef.current) {
      setTimeout(() => {
        cardRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 200);
    }
  }, [isExpanded, isAnimating]);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedProfile = await getUserProfile(entry.user_id);
      setProfile(fetchedProfile);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to load profile';
      setError(errorMessage);
      console.error('Failed to fetch profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = (updated: UserProfile) => {
    setProfile(updated);
  };

  // Use profile data if available (after expansion), otherwise use entry data (initial load)
  const displayName = profile
    ? (profile.show_email ? profile.email : (profile.display_name || profile.email))
    : (entry.show_email ? entry.email : (entry.display_name || entry.email));

  return (
    <Card
      ref={cardRef}
      className={`overflow-hidden ${entry.is_current_user ? 'border-primary/50 bg-primary/5' : ''}`}
    >
      {/* Card Header - Clickable */}
      <div
        className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Rank */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {getRankIcon(entry.rank)}
              <span className="font-bold text-lg">{entry.rank}</span>
            </div>

            {/* User Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium truncate">
                  {displayName}
                </span>
                {entry.is_current_user && <Badge variant="secondary">You</Badge>}
              </div>
            </div>

            {/* Upload Count */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="font-bold text-lg">{entry.upload_count}</span>
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-300 ease-in-out ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isAnimating && (
        <div
          className={`transition-all duration-300 ease-out overflow-hidden ${
            isExpanded
              ? 'animate-in slide-in-from-top-5 opacity-100 max-h-[2000px]'
              : 'animate-out slide-out-to-top-5 opacity-0 max-h-0'
          }`}
        >
          <div className="border-t bg-muted/20">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}

            {error && (
              <div className="text-center py-8">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            {profile && !loading && !error && (
              <LeaderboardRowExpanded
                profile={profile}
                onProfileUpdate={handleProfileUpdate}
              />
            )}
          </div>
        </div>
      )}
    </Card>
  );
};
